import { PublicIdSchemas } from '@idc-forms/id'
import { z } from 'zod'
import { COMPANY_TYPE } from '../entities/company/company'
import { optionalString, requiredString } from '../primitives/string'
import { InstantSchema } from '../primitives/time'
import { V2CursorPaginationSchema } from './http'

/**
 * Contrato de la administración v2 de empresas. Segundo catálogo del backoffice
 * en v2 (después de estados) y cabeza del grafo relacional de la ola 2: las
 * sociedades y agencias cuelgan de una empresa.
 *
 * `name` es único GLOBAL (case-insensitive) entre empresas vivas.
 *
 * DOS CAMPOS DE SISTEMA NO ENTRAN AL INPUT, deliberadamente:
 *
 * - `type` — `IDC`/`CVN` distingue a la empresa operativa de la fila CVN
 *   histórica, que el dominio protege del borrado. Por el API sólo nacen
 *   empresas `IDC` y una edición nunca cambia el tipo: aceptarlo en el wire
 *   dejaría convertir cualquier empresa en la de sistema, o al revés.
 * - `blocked` — marca de sistema que protege del borrado. Si viajara en el
 *   input, cualquiera se desbloquearía a sí mismo antes de borrar y la guarda no
 *   valdría nada.
 *
 * `default` SÍ es escribible y es una regla de negocio, no una marca de sistema:
 * hay exactamente una empresa predeterminada, así que marcar una desmarca a la
 * anterior y la fuerza activa.
 */

/**
 * Autoría wire-safe: el `_id` del usuario no cruza el adapter, así que se
 * publica su `usr_`. Va entera nullable porque el legacy ya guarda `createdBy:
 * null` en las filas sembradas, y la UI hace mucho que resuelve ese caso a «—».
 */
export const V2AdminCompanyAuthorSchema = z
  .object({
    id: PublicIdSchemas.user,
    fullname: z.string()
  })
  .nullable()

/** Fila del listado y del detalle. */
export const V2AdminCompanySchema = z.object({
  id: PublicIdSchemas.company,
  name: z.string().min(1),
  /** Número GIIN (FATCA). Cadena vacía cuando no se cargó. */
  giinNumber: z.string(),
  type: z.nativeEnum(COMPANY_TYPE),
  /** Única en el sistema: marcarla desmarca a la anterior y la fuerza activa. */
  default: z.boolean(),
  /** De sistema: una empresa bloqueada no se puede borrar. Sólo lectura. */
  blocked: z.boolean(),
  active: z.boolean(),
  createdBy: V2AdminCompanyAuthorSchema,
  updatedBy: V2AdminCompanyAuthorSchema,
  createdAt: InstantSchema,
  updatedAt: InstantSchema
})

/** Página cursor-paginada del listado (ADR 0019). */
export const V2AdminCompaniesPageSchema = z.object({
  data: z.array(V2AdminCompanySchema),
  pagination: V2CursorPaginationSchema
})

/**
 * Total y facetas con los mismos filtros allowlist del listado. Cada faceta
 * ignora el filtro de SU PROPIA dimensión (si se mirara a sí misma, al elegir un
 * valor el resto saldría en cero y el panel afirmaría que no existe); `total` sí
 * respeta la consulta completa.
 *
 * Las dos dimensiones son de baja cardinalidad y sirven para navegar: `type`
 * tiene dos valores y `active` dos.
 */
export const V2AdminCompaniesSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  active: z.object({
    true: z.number().int().nonnegative(),
    false: z.number().int().nonnegative()
  }),
  type: z.record(z.nativeEnum(COMPANY_TYPE), z.number().int().nonnegative())
})

/**
 * Entrada mínima del selector de empresas. `default` viaja porque los formularios
 * de sociedad, agencia, cliente y negociación preseleccionan la predeterminada.
 * Identifica por `cmp_` y nunca expone `_id` ni el uuid legacy.
 */
export const V2AdminCompanyLookupItemSchema = z.object({
  id: PublicIdSchemas.company,
  name: z.string().min(1),
  default: z.boolean()
})

export const V2AdminCompanyLookupSchema = z.object({
  data: z.array(V2AdminCompanyLookupItemSchema)
})

/**
 * Alta de empresa. `name` se recorta y debe ser único (case-insensitive) entre
 * empresas vivas. La empresa nace `IDC`: `type` y `blocked` son de sistema y no
 * viajan en el wire.
 */
export const V2CreateAdminCompanySchema = z.object({
  name: requiredString()
    .transform((value) => value.trim())
    .pipe(requiredString()),
  giinNumber: optionalString(),
  default: z.boolean().optional(),
  active: z.boolean().optional()
})

/** Edición parcial. Un id desconocido responde 404: nunca hace upsert. */
export const V2UpdateAdminCompanySchema = V2CreateAdminCompanySchema.partial()

export type V2AdminCompanyAuthor = z.infer<typeof V2AdminCompanyAuthorSchema>
export type V2AdminCompany = z.infer<typeof V2AdminCompanySchema>
export type V2AdminCompaniesPage = z.infer<typeof V2AdminCompaniesPageSchema>
export type V2AdminCompaniesSummary = z.infer<typeof V2AdminCompaniesSummarySchema>
export type V2AdminCompanyLookupItem = z.infer<typeof V2AdminCompanyLookupItemSchema>
export type V2AdminCompanyLookup = z.infer<typeof V2AdminCompanyLookupSchema>
export type V2CreateAdminCompanyInput = z.infer<typeof V2CreateAdminCompanySchema>
export type V2UpdateAdminCompanyInput = z.infer<typeof V2UpdateAdminCompanySchema>
